<template>
  <div v-if="device" class="device_profile">
    <div class="device_profile__left">
      <div class="line refer_info">
        <div v-if="versions_text" class="version" :title="versions_title">
          {{ versions_text }}
        </div>

        <div
            v-if="$store.getters.getPermissions.permissionGod === true && overlays.length"
            class="version overlays pointer"
            @click="show_overlays"
        >
          {{ overlays.map(overlay => overlay.name).join(', ') }}
        </div>

        <div class="icons">
          <span
              v-if="$store.getters.getPermissions.createReports === true"
              class="mdi mdi-file-chart icon"
              :title="translate('reports.create')"
              @click.prevent="create_report"
          />

          <span
              class="mdi mdi-book-open icon"
              title="Contacts"
              v-if="$store.getters.getPermissions.permissionGod  === true"
              @click.prevent="add_contact()"
          />

          <span
              class="mdi mdi-crosshairs-gps icon"
              :class="{'manual': minimap.source === 'manual'} "
              title="Coords"
              v-if="$store.getters.getPermissions.updateDeviceCoord  === true"
              @click.prevent="add_coordinates_manually()"
          />

          <span
              class="mdi mdi-history icon"
              :title="translate('soft_version_history ')"
              v-if="$store.getters.getPermissions.permissionGod === true"
              @click.prevent="show_version_history()"
          />

          <span
              class="mdi mdi-console-line icon"
              title="RPC Console"
              v-if="device.device_info.support_rpc && $store.getters.getPermissions.runDeviceRpc === true"
              @click.prevent="show_rpc()"
          />

          <span
              class="mdi mdi-speedometer icon"
              :title="translate('run_test')"
              v-if="$store.getters.getPermissions.runDeviceSelftest === true"
              @click.prevent="open_speedometer"
          />

          <span
              v-if="$store.getters.getPermissions.permissionSupport === true"
              class="mdi mdi-face-agent icon"
              :class="{ new_tickets: device.tickets?.open > 0 }"
              :title="translate('support.contact')"
              @click.prevent="new_ticket()"
          />

          <span
              v-if="$store.getters.getPermissions.permissionNotifications === true"
              class="mdi mdi-bell-ring-outline icon"
              :title="translate('notification.one')"
              @click.prevent="notification(device)"
          />
        </div>
      </div>

      <div class="line miniblocks">
        <div
            class="miniblock status_info card"
            :class="{
              online: status,
              offline: !status,
              backup: device.status_info.backup,
              blocked: device.status_info.block?.status,
            }"
        >
          <div class="miniblock__wrap" :title="device_online_title">
            <div class="icons">
              <span class="icon mdi mdi-access-point-off offline" v-if="!status"/>
              <span class="icon mdi mdi-access-point online" v-if="status"/>
              <span class="icon mdi mdi-server-network backup" v-if="device.status_info.backup"/>
              <span class="icon mdi mdi-lock-alert blocked" v-if="device.status_info.block?.status"/>
            </div>

            <div
                class="boot"
                v-if="device.status_info.online?.boot_location"
                :title="'ОС загружена с ' + device.status_info.online.boot_location"
            >
              <span class="mdi mdi-usb-flash-drive"/>
              <p> {{ device.status_info.online.boot_location }}</p>
            </div>

            <div class="text">
              <p v-if="status">
                {{ uptimeStr }}
              </p>
              <router-link v-else-if="get_last_day_online !== null" :to="get_last_day_online">
                {{ uptimeStr }}
              </router-link>
              <p v-else>
                {{ uptimeStr }}
              </p>
            </div>

            <div v-if="device.status_info.backup" class="backup">
              <p v-if="device.status_info.backup.ip">IP: {{ device.status_info.backup.ip }}</p>
              <p v-if="device.status_info.backup.server">Server: {{ device.status_info.backup.server }}</p>
            </div>

            <div class="footer" v-if="status && (Object.keys(miniblock_uptime).length > 0)">
              <span
                  v-if="miniblock_uptime.time"
                  :style="'color: ' + device.status_info.online.uptime < 60 * 10 ? '#b84141' : '#41B883'"
                  :title="device_uptime_title"
              >
                {{ translate("device.uptime") }}: {{ miniblock_uptime.time }}
              </span>

              <span v-if="miniblock_uptime.queue" :style="'color: ' + miniblock_uptime.queue > 1000 ? '#b84141' : '#41B883'">
                {{ translate("device.queue_size") }}: {{ miniblock_uptime.queue }}
              </span>
            </div>
          </div>
        </div>

        <div class="miniblock device_info card" v-if="$store.getters.getPermissions.viewDeviceModelInfo === true">
          <div class="miniblock__wrap">
            <p>
              <template v-if="device.device_info.model_info?.sku">
                Article:
                <b>
                  {{ device.device_info.model_info.sku }}
                </b>
                <span
                    class="mdi mdi-help-circle-outline pointer"
                    @click="show_model_info"
                />
              </template>
              <template v-else>
                {{ device.device_info.model_info?.model_name }}
              </template>
            </p>
            <p v-if="device.device_info.version?.fw">FW: {{ device.device_info.version?.fw }}</p>
            <p @dblclick="copy(device.mid)" v-if="$store.getters.getPermissions.permissionGod === true">
              Mid:
              {{ device.mid }}
            </p>
            <p @dblclick="copy(device.id)" v-if="$store.getters.getPermissions.permissionGod === true">
              Device ID:
              <b>
                {{ device.id }}
              </b>
            </p>

            <div class="add_info card" :class="{ active: !hide_app_info }">
              <p>Manufacturer: {{ device.device_info.model_info?.manufacturer }}</p>
              <p>Description: {{ device.device_info.model_info?.model_descr }}</p>
            </div>
            <span
                class="mdi mdi-pen edit"
                v-if="$store.getters.getPermissions.permissionGod === true"
                @click="update_device_sku()"
            />
          </div>
        </div>

        <div
            class="miniblock temper card"
            :class="{
            pointer: $store.getters.getPermissions.viewDeviceStats,
          }"
            v-if="monitor.ts"
            :title="get_date(monitor.ts)"
            @click.prevent="go_to_stats()"
        >
          <div class="top" v-if="$store.getters.getPermissions.viewDeviceChannelStatus === true">
            <div
                v-for="(channel, channel_key) in channel_status"
                :key="channel_key"
                class="channel"
            >
              <span class="channel_name">
                {{ channel_key }}
              </span>
              <TimeProgressCard
                  :status="true"
                  :info="channel"
                  :period="channel_key"
              />
            </div>

          </div>
          <div class="bottom">
            <p>
              <b>{{ translate("device.load") }}: </b>
              {{ monitor.cpu }} %
            </p>
            <p class="temper_param" v-if="monitor?.cpu_temp">
              <b>CPU:</b>
              {{
                +(monitor?.cpu_temp > 1000
                    ? monitor?.cpu_temp / 1000
                    : monitor?.cpu_temp)
              }}
              <span class="mdi mdi-temperature-celsius"/>
            </p>
            <p class="temper_param" v-if="monitor?.mother_temp">
              <b>board:</b> {{ (+monitor.mother_temp).toFixed(2) }}
              <span class="mdi mdi-temperature-celsius"/>
            </p>
          </div>
        </div>

        <div class="miniblock minimap card">
          <gmap-map
              v-if="minimap.show === true"
              :zoom="10"
              :center="minimap.coords"
              @click="click_find_distance"
              @rightclick="rightclick_find_distance"
              :options="{
              zoomControl: false,
              mapTypeControl: false,
              scaleControl: false,
              streetViewControl: false,
              rotateControl: true,
              fullscreenControl: true,
              disableDefaultUi: false
            }"
              style="width: 100%; height: 100px"
          >
            <gmap-marker
                :position="minimap.coords"
                :icon="
                  icon_status
                    ? { url: require('assets/icons/sistematics_online.png') }
                    : { url: require('assets/icons/sistematics_offline.png') }
                "
                :draggable="false"
                @altclick="copy_to_clipboard(minimap.coords.lat + ' ' + minimap.coords.lng)"
                @dblclick="copy_to_clipboard(minimap.coords.lat + ' ' + minimap.coords.lng)"
                @click="openInfoWindow(minimap.coords, 'first')"
                @rightclick="openInfoWindow(minimap.coords, 'second')"
            />

            <gmap-info-window
                :options="{
                maxWidth: 300,
                pixelOffset: { width: 0, height: -20 },
                content: minimap.info,
                strokeColor: '#FF0000',
              }"
                :position="minimap.coords"
                :opened="minimap.open"
                @closeclick="minimap.open = false"
            >
            </gmap-info-window>


            <!-- Элементы карты необходимые для линейки -->

            <gmap-marker
                v-for="(marker, index) in distance_markers"
                :key="'distance'+index"
                :position="marker"
                :icon="{ url: require('assets/icons/sistematics_dist.png')}"
                :draggable="false"
            />

            <gmap-polyline
              :path="distance_markers"
              :coords.sync="distance_markers"
              :options="{
                strokeColor:'#7f058f',
                strokeOpacity: 1.0,
                strokeWeight: 3,
                icons: [{
                  icon: {
                    path: 0,
                    scale: 3
                  },
                  offset: '1',
                  repeat: '100px'
                }]
              }"
            />

            <gmap-info-window
              :options="{
                maxWidth: 300,
                pixelOffset: {
                  width: 0,
                  height: -10
                },
                content: distanceInfo,
                strokeColor:'#FF0000'
              }"
            :position="distancePos"
            :opened="distanceOpen"
            @closeclick="clear_find_distance()"
            >
            </gmap-info-window>

            <!-- Конец элементов карты необходимых для линейки -->

          </gmap-map>

          <div class="info_location" v-if="minimap.country || minimap.city">
            <span class="country">
              {{ minimap.country }}, {{ minimap.city }}
            </span>
          </div>

          <div
              v-if="$store.getters.getPermissions.updateDeviceCoord === true && !minimap?.coords?.lat && !minimap?.coords?.long"
              class="add_coords"
              @click="add_coordinates_manually()"
          >
            <span class="mdi mdi-plus-box"/>
          </div>

          <div v-if="minimap.show === null" class="spinner_wrap">
            <scale-loader/>
          </div>

          <div class="no_data" v-if="minimap.show === false">
            <p> {{ translate("no_location_data") }} </p>
          </div>
        </div>
      </div>

      <div class="line ifaces" v-if="iface_status.length > 0">
        <router-link
            v-for="(iface, key) in iface_status"
            :key="key"
            class="card"
            :class="[iface.status > 0 ? 'active_color' : 'inactive_color', device.status_info.online?.status ? '' : 'inactive_back']"
            :to="'/device/' + device.fid + '/stats?&speed=false&rssi=false&temper=false&ifaces=true&cpu=false&simcard_status=false'"
            :title="get_title_iface(device.status_info.online?.status, iface)"
        >
          <div class="top">
            <span>
              {{ iface.iface }}
            </span>
            <span>
              {{ iface_time(iface.ts_update) }}
            </span>
          </div>
          <p v-if="iface.address"> {{ iface.address }} </p>
          <p v-if="iface.config"> {{ iface.config }} </p>
        </router-link>
      </div>

      <div class="line billing">
        <div
            v-if="client_info.traffic"
            class="card traffic__block"
        >
          <div class="traffic__wrap" >
            <div
                class="traffic_line"
                v-for="(traffic_item, key) in client_info.traffic" :key="key"
                :title="graph_title(traffic_item)"
            >
              <div class="roaming" v-if="traffic_item.roaming !== null">
                <span v-if="traffic_item.roaming === 1" class="mdi mdi-earth on"/>
                <span v-if="traffic_item.roaming === 0" class="mdi mdi-earth off"/>
              </div>

              <div class="traffic">
                <div v-if="traffic_item && !traffic_item.is_unlim" class="graph_wrap">
                  <span class="graph" :style="trafficBalance(traffic_item).style"/>
                </div>
                <div v-else class="graph_wrap">
                  <span class="mdi mdi-infinity"></span>
                </div>

                <p class="rest" v-if="traffic_item.is_unlim">
                  {{ traffic_item.packet_name }}
                </p>
                <p class="rest" v-else>
                  {{ traffic_item.packet_name }}
                  <b :style="'color:' + trafficBalance(traffic_item).color">
                    {{ trafficBalance(traffic_item).traffic }}
                  </b>
                  {{ translate("simcard_devices.out_of") }}
                  <b>
                    {{ traffic_item.limit > 1024 * 5 ? Math.round(traffic_item.limit / 1024) + ' GB' : traffic_item.limit + ' MB' }}
                  </b>
                </p>

              </div>
            </div>
          </div>

          <span
              v-if="show_button_add_packet"
              class="btn btn-success btn-fill mdi mdi-plus add_packet"
              @click="add_new_packet"
              :title="translate('traffic_packet_list.tariff_packet_item.add_new_packet')"
          />

        </div>

        <div v-if="client_info.order && $store.getters.getPermissions.permissionGod === true" class="card client_order">
          <div class="client_order__line">
            <p class="block_title left">{{ translate("client_order.order") }}</p>
            <router-link
                :to="'/production/order_list?&search=' + fid"
                v-if="client_info.order"
                class="link right"
            >
              <span class="mdi mdi-list-status"></span>
            </router-link>
          </div>

          <div class="client_order__line">
            <router-link class="left" :to="{ path: '/client_order/'+ client_info.order.client_order_id +'/info' }">
              {{ client_info.order.name }}
            </router-link>
          </div>

          <div class="client_order__line">
            <p class="left">
                {{ client_info.order.date_send ? `${translate("client_order.sended")}: ${client_info.order.date_send}` : "" }}
            </p>

            <router-link class="right" v-if="$store.getters.getPermissions.permissionGod === true"
                         :to="{ path: '/client/' + client_info.order.client_id }">
              {{ client_info.order.client_name }}
            </router-link>
            <p class="right" v-else>
              {{ client_info.order.client_name }}
            </p>
          </div>
        </div>

        <div
            class="card tariff"
            :class="select_tariff_info(client_info)"
        >
          <div class="tariff__line">
            <p class="left">{{translate("device.section.tariffication")}}</p>
            <router-link
                :to="'/tariffication/tariffed_devices?&fid=' + fid"
                v-if="client_info.tariff"
                class="link right"
            >
              <span class="mdi mdi-list-status"></span>
            </router-link>
          </div>

          <div class="tariff__line">
            <div class="tariffication">
              <span class="mdi mdi-currency-rub" v-if="client_info.tariff?.status"></span>
              <router-link :to="client_info.tariff?.id ?  '/tariffed_device/' + client_info.tariff?.id : '/tariffication/tariffed_devices?&fid=' + fid">
                <p>
                  {{ client_info.tariff?.text }}
                </p>
              </router-link>
            </div>
          </div>

          <div class="tariff__line">
            <div class="left">
              <p> {{ client_info.tariff?.order_info }} </p>
            </div>

            <div class="right">
              <template>
                <router-link
                    v-if="client_info.tariff?.client?.client_id && $store.getters.getPermissions.permissionGod === true"
                    :to="{ path: '/client/' + client_info.tariff?.client?.client_id }"
                    :class="diff_client.class"
                    :title="diff_client.title"
                >
                  {{ client_info.tariff?.client?.client_name ? client_info.tariff.client.client_name : ""}}
                </router-link>
                <p v-else :class="diff_client.class" :title="diff_client.title">
                  {{ client_info.tariff?.client?.client_name ? client_info.tariff.client.client_name : ""}}
                </p>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div class="line problems">
        <div class="blocked card" v-if="device.status_info.block?.status">
          <div class="block_left">
            <p>
              {{ translate("device.status") }} :
              {{ device.status_info.block.text_status }}
            </p>
            <p v-if="device.status_info?.block?.reason">
              {{ translate("device.reason") }} : {{ device.status_info.block.reason }}
            </p>
          </div>
          <div class="block_right">
            <router-link v-if="device.status_info.block.user_data"
                         :to="'/user/' + device.status_info.block.user_data.user_name">
              {{ device.status_info.block.user_data.user_name }}
            </router-link>
          </div>

        </div>
      </div>

      <div
          class="line simcard_list"
          v-if="$store.getters.getPermissions?.viewDeviceSimcardList === true"
      >
        <SimCardList
            v-if="simcard_list.length > 0"
            :device_id="device.id"
            :device_fid="device.fid"
            :simcard_list="prepared_simcards"
            :params_list="params_list"
            :traffic_info="traffic_info"
            :simcard_binding="simcard_binding"
            :network_settings="network_settings"
            :status="status"
            @update_simcard_binding="simcard_bilding_index"
        />

      </div>

      <div class="line servers" v-if="device.servers.length > 0">
        <ServerList
            :device_servers="device.servers"
            :network_settings="network_settings"
        />
      </div>

      <div class="line main_info" :class="{ 'main_info--two-columns': !device_contacts.length || $store.getters.getPermissions.permissionGod !== true }">
        <div class="main_info__wrap">
          <div class="header_main_info">
            <p class="title">{{ translate("device.main_info") }}</p>

            <template v-if="client_info.client">
              <router-link
                  v-if="$store.getters.getPermissions.permissionGod === true"
                  :to="{ path: '/client/' + client_info.client.client_id }"
                  class="title"
              >
                {{ client_info.client.client_name }}
              </router-link>
              <p v-else class="title">
                {{ client_info.client.client_name }}
              </p>
            </template>
          </div>

          <div class="card">
            <span
                class="mdi mdi-pen edit"
                v-if="$store.getters.getPermissions.editDeviceProfile"
                @click="edit_device_profile()"
            />

            <p v-if="client_info && client_info.service_id && client_info.service_name">
              {{ client_info.service_name }}: {{ client_info.service_id }}
            </p>

            <p>{{ translate("device.name") }}: {{ device.user_info.name2 }}</p>

            <p v-if="$store.getters.getPermissions.permissionGod === true && device.user_info.name">
              {{ translate("device.hidden_name") }}: {{ device.user_info.name }}
            </p>

            <div class="groups">
              <p>{{ translate("group.one") }}:</p>
              <div class="links">
                <div
                    v-for="(group, key) in groups_device"
                    :key="key"
                    class="link"
                >
                  <router-link :to="{ path: '/device_list?group=' + group.id }">
                    {{ group.name }}
                  </router-link>
                  <router-link
                      v-if="$store.getters.getPermissions.permissionGod === true"
                      :to="{ path: '/device_groups?group_id=' + group.id }"
                  >
                    <span class="mdi mdi-pen"></span>
                  </router-link>
                  <span class="delim">
                    {{ key === groups_device.length - 1 ? "" : "->" }}
                  </span>
                </div>
              </div>
            </div>

            <p v-if="device.user_info.place">{{ translate("device.place") }}: {{ device.user_info.place }}</p>
            <p v-if="device.user_info.place_full">{{ translate("device.place_full") }}: {{
                device.user_info.place_full
              }}</p>
            <p v-if="device.user_info.desc">{{ translate("device.desc") }}: {{ device.user_info.desc }}</p>
            <p v-if="device.user_info.add_desc">{{ translate("device.desc_add") }}: {{ device.user_info.add_desc }}</p>
          </div>
        </div>

        <div class="main_info__wrap" v-if="Object.keys(network_settings).length > 0">
          <div class="header_main_info">
            <p class="title">{{ translate("device.network_settings.block") }}</p>
          </div>

          <div class="card network_setting">
            <span
                class="mdi mdi-pen edit"
                v-if="$store.getters.getPermissions.editDeviceProfile"
                @click="edit_network_settings()"
            />
<!--            <p>{{`${translate('device.config_name')}: ${network_settings.config_name}`}} </p>-->
            <p v-if="network_settings.network_settings?.network_type">{{`${translate('device.network_type')}: ${network_settings.network_settings.network_type}`}} </p>
            <p v-if="network_settings.network_settings?.vlan">{{`${translate('device.vlan')}: ${network_settings.network_settings.vlan ? network_settings.network_settings.vlan : ""}`}} </p>
            <p v-if="network_settings.network_settings?.dhcp_summary">{{ network_settings.network_settings.dhcp_summary }}</p>
            <template v-if="Number(network_settings.wifi?.status) !== 0">
              <p v-if="network_settings.wifi?.ssid_name">{{`${translate('device.wifi_ssid2')}: ${network_settings.wifi.ssid_name}`}} </p>
              <p v-if="network_settings.wifi?.password">{{`${translate('device.wifi_password2')}: ${network_settings.wifi.password}`}} </p>
            </template>
            <p v-if="network_settings.mmadmin?.login">{{`${translate('device.mmadmin_local')}: ${network_settings.mmadmin.login} / ${network_settings.mmadmin.password}`}} </p>
          </div>
        </div>

        <div class="main_info__wrap" v-if="device_contacts.length > 0 && $store.getters.getPermissions.permissionGod === true">
          <div class="header_main_info">
            <p class="title">{{ translate("device.contacts.title") }}</p>
          </div>

          <div class="card contacts">
            <span
                class="mdi mdi-pen edit"
                v-if="$store.getters.getPermissions.permissionGod === true"
                @click="add_contact()"
            />

            <p v-for="(contact, key) in device_contacts" :key="key" class="contact">
              <span>
                {{ get_label_contact(contact.type).label }}:
              </span>
              <span v-if="get_type_contact(contact.type).type === 'text'">
                {{ contact.value }}
              </span>
              <a v-else-if="get_type_contact(contact.type).type === 'phone'" :href="'tel:' + contact.value">
                {{ contact.value }}
              </a>
              <a v-else-if="get_type_contact(contact.type).type === 'email'" :href="'mailto:' + contact.value">
                {{ contact.value }}
              </a>
              <a v-else-if="get_type_contact(contact.type).type === 'web'" :href="contact.value" target="_blank">
                {{ contact.value }}
              </a>
            </p>
          </div>
        </div>
      </div>

      <div class="line text-center">
        <span
            style="margin-left: 20px"
            class="btn btn-danger btn-wd"
            @click="blockedDevice()"
            v-if=" device.status_info.block?.button_visible && $store.getters.getPermissions.runDeviceBlock === true"
        >
          {{ !device.status_info.block?.status ? translate("block") : translate("unblock") }}
        </span>
      </div>
    </div>

    <div class="device_profile__right">
      <DiadocObjectLinks
          v-if="$store.getters.getPermissions.permissionCommerceGod === true"
          :diadoc_object_links="diadoc_object_links"
      />
      <ClientRequestDevice
          v-if="$store.getters.getPermissions.viewClientRequests === true"
          :device_fid="device.fid"
          :device_id="device.id"
          :device_name="device.user_info.name2"
          :client_request="client_request"
          :available_actions="available_actions"
          :simcard_list="prepared_simcards"
      />

<!--      <BlockedSimcardDevices-->
<!--          v-if="$store.getters.getPermissions.permissionGod === true"-->
<!--          :device_fid="device.fid"-->
<!--      >-->
<!--      </BlockedSimcardDevices>-->

      <Summary
          type="device"
          :info="device"
      />
    </div>
  </div>
</template>
<script>
import Overlays from "./Modal/Overlays/Index.vue";
import ModalReports from "components/UIComponents/Reports/Reports.vue";
import EditDeviceProfile from "./Modal/EditDeviceProfile/Index.vue";
import EditNetworkSettings from "./Modal/EditNetworkSettings/Index.vue";
import ShowModelInfo from "./Modal/ShowModelInfo/Index.vue"
import NewTicket from "@/views/Dashboard/General/Supports/TicketList/Modal/NewTicket.vue";
import Summary from "components/UIComponents/Summary/Summary.vue";
import RPCModal from "./Modal/RPC/Index.vue";

import BlockDevice from "./Modal/BlockDevice/Index.vue";
import SimCardList from "./SimcardList/Index.vue";
import NotificationEdit from "@/views/Dashboard/General/Notification/Tabs/DeviceNotification/Item/Edit/Index.vue";
import SpeedTest from "./Modal/SpeedTest/Index.vue";
import HistoryModal from "./Modal/History/Index.vue";
import UpdateContact from "./Modal/Contacts/Index.vue";
import DeviceCoord from "./Modal/Coords/Index.vue";
import TimeProgressCard from "components/UIComponents/Charts/TimeProgressCard.vue";
import ClientRequestDevice from "./ClientRequestDevice/Index.vue";
import BlockedSimcardDevices from "./BlockedSimcardDevices/Index.vue";
import BuyTariffPacketItem from "../Tariffication/Modal/BuyTariffPacketItem.vue";
import DiadocObjectLinks from "./DiadocObjectLinks/DiadocObjectLinks.vue";
import UpdateDeviceSku from "./Modal/UpdateDeviceSku/UpdateDeviceSku.vue";

import ServerList from './ServerList/Index.vue'

export default {
  components: {
    TimeProgressCard,
    SimCardList,
    Summary,
    UpdateContact,
    ClientRequestDevice,
    BlockedSimcardDevices,
    ServerList,
    DiadocObjectLinks
  },

  props: {
    device: {
      type: [Array, Object],
      default: () => [],
    },
    simcard_list: {
      type: [Array, Object],
      default: () => [],
    },
    device_monitor: {
      type: [Array, Object],
      default: () => [],
    },
    client_request: {
      type: [Object],
      default: () => {},
    },
    available_actions: {
      type: [Array],
      default: () => [],
    }
  },

  data() {
    return {
      hide_app_info: true,
      uptimeStr: "",
      timer_res: null,

      minimap: {
        open: false,
        coords: {
          lng: 0,
          lat: 0,
        },
        source: "",
        info: "",
        show: null,
        city: null,
        country: null,
        ts: null,
      },

      simcard_binding: {},
      traffic_info: {},
      traffic_request_id: 0,

      miniblock_uptime:{},
      miniblock_uptime_time: this.device.status_info.online?.uptime,

      device_contacts: [],
      device_contacts_types: [],

      channel_status: {},

      client_info: {},
      network_settings: {},

      distanceInfo: '',
      distanceOpen: false,
      distancePos: {lat: 0, lng:0},
      distance_markers: [],

      diadoc_object_links: []
    };
  },

  created() {
    this.start_init()
  },

  computed: {
    params_list(){
      return {
        "soft0": {class: "mdi-sim-outline", label: "Soft 0"},
        "soft1": {class: "mdi-sim-outline", label: "Soft 1"},
        "soft2": {class: "mdi-sim-outline", label: "Soft 2"},
        "allow_international": {class: "mdi-earth", label: "Allow international"},
        "gps": {class: "mdi-crosshairs-gps", label: "GPS"},
        "backup": {class: "mdi-server-network", label: "Backup"},
        "remote": {class: "mdi-remote", label: "Remote sim"},
        "apn": {class: "", label: "APN"}
      }

    },
    prepared_simcards() {
      let simcard_list = [...this.simcard_list];

      const lastTsBySlot = simcard_list.reduce((acc, item) => {
        const slot = item.slot;
        const ts = Number(item.ts_sync) || 0;

        if (acc[slot] == null || ts > acc[slot]) acc[slot] = ts;
        return acc;
      }, {});

      simcard_list = simcard_list.map((item) => {
        let config = this.check_config(item.slot)
        let params = {}
        const is_last_in_slot = (Number(item.ts_sync) || 0) === (lastTsBySlot[item.slot] || 0);

        if (config && is_last_in_slot){
          Object.keys(this.params_list).forEach((param) => {
            params[param] = config.indexOf(param) > -1
          })
        }
        const hasTrueParams = Object.values(params).some(value => value === true)

        return {
          ...item,
          ...params,
          clear_params: !hasTrueParams,
          is_last_in_slot,
          bind: this.check_binding(item),
          power: this.check_power(item.slot, is_last_in_slot),
          traffic: this.traffic_info[item.imsi],
        }
      })

      return simcard_list
    },

    diff_client(){
      let resp = {}
      if (this.client_info.tariff?.client?.client_name !== this.client_info.client?.client_name){
        resp = {class: "diff", title: this.translate("device.diff_client")}
      } else {
        resp = {class: "", title: ""}
      }
      return resp
    },
    overlays() {
      return this.device?.device_info?.overlays || [];
    },
    versions_obj() {
      return this.device?.device_info?.version || null;
    },

    versions_text() {
      const v = this.versions_obj;
      if (!v || typeof v !== "object") return "";
      return Object.entries(v).map(([k, val]) => `${k}: ${val}`).join(", ");
    },

    versions_title() {
      const v = this.versions_obj;
      if (!v || typeof v !== "object") return "";
      return Object.entries(v).map(([k, val]) => `${k}: ${val}`).join("\n");
    },
    final_distance() {
      let lat1,lon1,lat2,lon2
      for (let marker of this.distance_markers) {
        if (marker.type === 'first') {
          lat1 = marker.lat
          lon1 = marker.lng
        }else if (marker.type === 'second') {
          lat2 = marker.lat
          lon2 = marker.lng
        }
      }

      let R = 6371000; // Radius of the earth in m
      let dLat = this.deg2rad(lat2-lat1);  // deg2rad below
      let dLon = this.deg2rad(lon2-lon1);
      let a =
          Math.sin(dLat/2) * Math.sin(dLat/2) +
          Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
          Math.sin(dLon/2) * Math.sin(dLon/2)
      ;
      let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      let d = R * c; // Distance in m
      return Math.trunc(d)
    },
    status(){
      return this.device.status_info.online?.status
    },
    get_last_day_online() {
      if (!this.device.status_info.online) return null

      let hours12 = 43200 //Количество секунд в 12 часах
      let ts_start = this.device.status_info.online.status_ts - hours12
      let ts_end = this.device.status_info.online.status_ts + hours12

      return '/device/' + this.device.fid + '/stats?&simcard_status=true&ts_start=' + ts_start + "&ts_end=" + ts_end
    },
    show_button_add_packet(){
      for (const traffic of this.client_info.traffic) {
        if (!traffic.is_unlim){
          if (Number(traffic.limit) === 0) return true
          if (Number(traffic.value) >= 0 && Number(traffic.limit) > 0){
            if ((Number(traffic.value) / Number(traffic.limit)) < 0.5) return true
          }
        }
      }
      return false
    },
    device_online_title(){
      let string = ""
      if (this.device.status_info.online?.status_ts){
        this.device.status_info.online.status === 0 ? string += this.translate('device.off_since') : string += this.translate('device.on_since')
        string += " " + this.get_date(this.device.status_info.online.status_ts)
      }
      return string
    },

    device_uptime_title(){
      let string = ""
      if (this.device.status_info.online?.uptime){
        this.device.status_info.online.status === 0 ? string += this.translate('device.off_since') : string += this.translate('device.on_since')
        string += " " + this.get_date(Math.round(new Date().getTime() / 1000) - this.device.status_info.online.uptime)
      }
      return string
    },
    iface_status(){
      if (!this.device.status_info.ifaces) return []
      return this.device.status_info.ifaces.map((item) => {
        let iface = {...item}
        if (this.device.config_params.iface && Object.keys(this.device.config_params.iface).indexOf(iface.iface) > -1){
          iface.config = this.device.config_params.iface[iface.iface]
        }
        return iface
      })
    },
    monitor() {
      let monitor = { ...this.device_monitor };
      let cpu_count = this.device.device_info.model_info ? +this.device.device_info.model_info?.cpu_count : 4;
      monitor.cpu = ((parseFloat(monitor.cpu) * 100) / cpu_count).toFixed(2);
      return monitor;
    },
    fid() {
      return this.device.fid;
    },
    icon_status() {
      return (Math.round(new Date().getTime() / 1000) - this.minimap.ts < 6 * 60 * 60 ); //последняя полученная точка была раньше 6 часов?
    },
    groups_device() {
      let groups = [];
      let group_id = this.device.user_info.group_id;
      let this_group = this.$store.getters.getDeviceGroupById(group_id)
      if (this_group.parent !== -1) {
        for (let id of this.$store.getters.getPathGroupById(group_id)) {
          let group = this.$store.getters.getDeviceGroupById(id)
          if (group.name !== "unknown"){
            groups.push({ id: group.id, name: group.name });
          }
        }
      }
      groups.unshift({ id: this_group.id, name: this_group.name });
      return groups.reverse();
    },
  },

  watch: {
    simcard_list: {
      immediate: true,
      handler() {
        this.get_device_simcard_traffic()
      }
    },
    fid() {
      this.get_device_simcard_traffic()
      this.start_init()
    },
    device: {
      deep: true,
      handler() {
        this.start_init()
        // this.change = !this.init;
        // this.$emit("change", this.change);
        // this.init = false; // первое изменение считаем инициализацией
      },
    },

    status() {
      this.update_uptime()
    },
  },

  destroyed() {
    clearInterval(this.interval_channel_status);
    clearTimeout(this.timer_res);
  },

  methods: {
    show_overlays() {
      if (this.$store.getters.getPermissions.permissionGod !== true) return
      const content = this.$createElement(Overlays, {
        props: { overlays: this.overlays },
        on: { close: () => this.$modal.hide('device_overlays') }
      })
      this.$modal.show({ render: () => content }, {}, {
        width: 1100, height: 'auto', adaptive: true, scrollable: true, clickToClose: true, name: 'device_overlays'
      })
    },
    create_report() {
      let t = this.$createElement(ModalReports, {
        props: { device_ids: [this.device.id] },
        on: { close: () => this.$modal.hide('modal_reports_device') }
      })
      let temp = { render() { return t } }
      this.$modal.show(temp, {}, {
        height: 'auto', width: 800, adaptive: true, clickToClose: true,
        scrollable: true, name: 'modal_reports_device'
      })
    },
    check_power(dev_slot, is_last_in_slot) {
      if (dev_slot && is_last_in_slot){
        let slot = dev_slot.toLowerCase()
        if (this.network_settings?.simcards?.power) {
          return !!this.network_settings?.simcards?.power[slot]
        }
      }
      return true
    },
    check_config(dev_slot){
      if (dev_slot){
        let slot = dev_slot.toLowerCase()
        let settings = []

        if (this.network_settings?.simcards) {
          Object.keys(this.network_settings.simcards).forEach(setting => {
            if (this.network_settings.simcards[setting][slot]) settings.push(setting)
          })
        }
        return settings
      }
    },
    check_binding(simcard) {
      if (!this.simcard_binding.items) return false;
      for (let simcard_item of this.simcard_binding.items) {
        if (simcard.imsi === simcard_item.imsi) {
          return true;
        }
      }
      return false;
    },
    get_device_simcard_traffic(){
      const request_id = ++this.traffic_request_id
      this.traffic_info = {}
      if (!this.$store.getters.getPermissions.permissionGod || !this.device.fid || !this.simcard_list.length) return
      let imsi_list = this.simcard_list.map((simcard) => {
        if (simcard.active === 1 || Number(simcard.ts_sync) > Date.now() / 1000 - 86400){ // Выбираем активные симкарты либо те что были за последние сутки онлайн
          return String(simcard.imsi)
        }
      }).filter((element) => { return element !== undefined })

      this.$http.v1.getDeviceSimcardTraffic(this.device.fid, {imsi_list: imsi_list}, (response) => {
        if (request_id !== this.traffic_request_id) return
        let traffic_info = response.data
        this.simcard_list.forEach((item) => {
          if (Object.keys(response.data).indexOf(String(item.imsi)) > -1){
            traffic_info[item.imsi].slot = item.slot
            traffic_info[item.imsi].mno = item.mno
            traffic_info[item.imsi].iccid = item.iccid
          }
        })
        this.traffic_info = traffic_info
      })
    },

    get_diadoc_object_links_index(){
      const params = { device_id: this.device.id }
      this.$http.v1.getDiadocObjectLinksIndex(params, (response) => {
        this.diadoc_object_links = response.data;
      });
    },
    get_title_iface(device_status, iface){
      let title = ""
      if (device_status === 1){
        if (iface.status === 1) title = `${this.translate("device.ifaces_title.online", {ts_change: this.get_date(iface.ts_status_change)})}`
        else if (iface.status === 0) title = `${this.translate("device.ifaces_title.offline", {ts_change: this.get_date(iface.ts_status_change)})}`
      } else if (device_status === 0){
        if (iface.status === 1) title = `${this.translate("device.ifaces_title.online", {ts_change: this.get_date(iface.ts_status_change)})}`
        if (iface.status === 0) title = `${this.translate("device.ifaces_title.off")}`
      }
      return title
    },
    update_device_sku(){
      let t = this.$createElement(UpdateDeviceSku, {
        props: {
          device_fid: this.device.fid,
          device_sku: this.device.device_info?.model_info?.sku,
        },
        on: {
          close: () => {
            this.$modal.hide("add_contact_modal");
          },
          update: () => {
            this.$store.dispatch('updateDeviceProfile', this.device.fid)
            this.$modal.hide("add_contact_modal");
          },
        },
      });
      let temp = {
        render() {
          return t;
        },
      };
      this.$modal.show(
          temp,
          {},
          {
            height: "auto",
            adaptive: true,
            clickToClose: true,
            scrollable: true,
            name: "add_contact_modal",
          }
      );
    },
    openInfoWindow (item, type) {
      if (event.ctrlKey) this.add_distance({ lat: item.lat, lng: item.lng }, type)
      this.minimap.open = !this.minimap.open
    },
    deg2rad(deg) {
      return deg * (Math.PI/180)
    },
    clear_find_distance(){
      this.distanceOpen = false
      this.distance_markers = []
    },
    click_find_distance(event){
      if (event.domEvent.ctrlKey) this.add_distance({lat: event.latLng.lat(), lng: event.latLng.lng()}, "first")
    },
    rightclick_find_distance(event){
      if (event.domEvent.ctrlKey) this.add_distance({lat: event.latLng.lat(), lng: event.latLng.lng()}, "second")
    },
    add_distance(elem, type){
      this.distance_markers = this.distance_markers.filter((elem) => {
        if (elem.type !== type) return elem
      })

      let coord = {
        lat: elem.lat,
        lng: elem.lng,
        type: type
      }
      this.distance_markers.push(coord)

      this.openInfoWindowDistance(coord)
    },
    openInfoWindowDistance (item) {
      if (this.final_distance) {
        this.distancePos = item
        this.distanceOpen = true
        if (this.final_distance < 999) {
          this.distanceInfo = this.final_distance + ' м'
        }else if (this.final_distance > 4999) {
          this.distanceInfo = (this.final_distance/1000) .toFixed(1) + ' км.'
        }else{
          this.distanceInfo = (this.final_distance/1000).toFixed(2) + ' км.'
        }
      }
    },

    start_init () {
      if (this.timer !== null) {
        clearTimeout(this.timer)
      }
      this.timer = setTimeout(this.init, 100)
    },
    show_model_info(){
      let t = this.$createElement(ShowModelInfo, {
        props: {
          device_fid: this.device.fid,
          device_name: this.device.user_info.name2,
        },
        on: {
          close: () => {
            this.$modal.hide("show_model_info");
          },
        },
      });
      let temp = {
        render() {
          return t;
        },
      };
      this.$modal.show(
          temp,
          {},
          {
            height: "auto",
            adaptive: true,
            clickToClose: true,
            scrollable: true,
            name: "show_model_info",
          }
      );
    },
    init(){
      this.update_uptime()
      this.get_minimap()
      this.get_client_info()
      this.get_network_settings()

      if (this.$store.getters.getPermissions.permissionGod === true) {
        this.simcard_bilding_index()
        this.device_contact_view()
      }

      if (this.$store.getters.getPermissions.viewDeviceChannelStatus === true){
        this.get_channel_status()
      }

      if (this.$store.getters.getPermissions.permissionCommerceGod === true){
        this.get_diadoc_object_links_index()
      }
    },
    edit_network_settings() {
      let t = this.$createElement(EditNetworkSettings, {
        props: {
          device_fid: this.device.fid,
          network_settings: this.network_settings
        },
        on: {
          close: () => {
            this.$modal.hide("edit_network_settings");
          },
          update: (data) => {
            this.$store.dispatch('updateDeviceProfile', this.device.fid)
            this.$modal.hide("edit_network_settings");
          },
        },
      });
      let temp = {
        render() {
          return t;
        },
      };
      this.$modal.show(
          temp,
          {},
          {
            height: "auto",
            width: 800,
            adaptive: true,
            clickToClose: true,
            scrollable: true,
            name: "edit_network_settings",
          }
      );
    },
    get_client_info(){
      this.$http.v1.getClientInfoDevice(this.device.fid, (response) => {
        this.client_info = response.data;
      });
    },
    get_network_settings(){
      this.$http.v1.getNetworkSettingsDevice(this.device.fid, (response) => {
        this.network_settings = response.data;
      });
    },
    add_new_packet() {
      if (!this.client_info?.tariff?.id) return
      let t = this.$createElement(BuyTariffPacketItem, {
        props: {
          tariffication_info: this.client_info.tariff,
          tariffication_id:  this.client_info.tariff.id
        },
        on: {
          close: () => {
            this.$modal.hide('buy_packet_item')
          },
          update: () => {
            this.$store.dispatch('updateDeviceProfile', this.device.fid)
            this.global_notify( "info", this.translate("device.completed_successfully"));
            this.$modal.hide('buy_packet_item')
          }
        }
      })
      let temp = { render() { return t } }
      this.$modal.show(temp, {}, {
        height: "auto",
        width: 800, //TODO in const to
        adaptive: true,
        clickToClose: true,
        name: 'buy_packet_item'
      })
    },
    select_tariff_info(client_info) {
      if (!client_info.tariff) return ""
      return client_info.tariff.status === 0 ? "off" : client_info.tariff.date_end ? "ended" : "on";
    },
    get_channel_status() {
      this.$http.v1.getDeviceChannelStatus(this.device.fid, (response) => {
        this.channel_status = response.data;
      });

      clearInterval(this.interval_channel_status);

      this.interval_channel_status = setInterval(() => {
        this.$http.v1.getDeviceChannelStatus(this.device.fid, (response) => {
          this.channel_status = response.data;
        });
      }, this.$store.getters.getDeviceProfileUpdate)
    },
    graph_title(data){
      return `${data.value} MB ${this.translate('simcard_devices.out_of')} ${data.limit} MB`
    },
    iface_time(ts_time){
      let timeUp = Math.round(Date.now() / 1000) - (ts_time + 1);
      return timeUp < 0 ? this.uptimeToStr(0) : this.uptimeToStr(timeUp);
    },
    trafficBalance(data) {
      if (!data) return null

      let traffic_GB = +data.value / 1024
      let percent = Math.round((+data.value / +data.limit) * 100)
      let color = percent <= 10 ? '#FF0000' : percent >= 25 ? '#01AA88' : '#FFA200'

      return {
        traffic: traffic_GB > 5 ? Math.round(traffic_GB) + " GB" : data.value + " MB",
        style: "--p:" + percent + ";" + ";--c:" + color + ";--b:3px;--lw:" + percent + "%",
        percent,
        color
      }
    },
    update_uptime(){
      clearInterval(this.timer_res);
      this.device_uptime();
      this.timer_res = setInterval(
          this.device_uptime,
          this.$store.getters.getCountdown
      );
    },
    get_type_contact(type){
      return this.device_contacts_types.find((elem) => {
        if (elem.id === type) return elem.type
      })
    },
    get_label_contact(type){
      return this.device_contacts_types.find((elem) => {
        if (elem.id === type) return elem.label
      })
    },
    add_contact(){
      let t = this.$createElement(UpdateContact, {
        props: {
          fid: this.device.fid,
          types: this.device_contacts_types,
          contacts: this.device_contacts
        },
        on: {
          close: () => {
            this.$modal.hide("add_contact_modal");
          },
          update: () => {
            this.device_contact_view()
            this.$modal.hide("add_contact_modal");
          },
        },
      });
      let temp = {
        render() {
          return t;
        },
      };
      this.$modal.show(
          temp,
          {},
          {
            height: "auto",
            adaptive: true,
            clickToClose: true,
            scrollable: true,
            name: "add_contact_modal",
          }
      );
    },
    device_uptime() {
      if (this.device.status_info.online) {
        let timeUp = Math.round(Date.now() / 1000) - (this.device.status_info.online?.status_ts + 1);
        this.uptimeStr = timeUp < 0 ? this.uptimeToStr(0) : this.uptimeToStr(timeUp);
        this.miniblock_uptime_time += 1;

        this.miniblock_uptime.time = this.uptimeToStr(this.miniblock_uptime_time)
        this.miniblock_uptime.queue = this.device.status_info.online?.queue_size ? this.device.status_info.online.queue_size : null
      } else {
        this.uptimeStr = this.translate("device.off_forever");
      }
    },
    simcard_bilding_index() {
      this.$http.v1.getSimcardBindingIndex(this.device.fid, (response) => {
        this.simcard_binding = response.data;
      });
    },

    device_contact_view(){
      this.$http.v1.getDeviceContact(this.device.fid, (response) => {
        this.device_contacts = response.data.data
        this.device_contacts_types = response.data.types
      });
    },

    go_to_stats() {
      if (this.$store.getters.getPermissions.viewDeviceStats) {
        this.$router.push({
          path:
            "/device/" +
            this.$route.params.device_id +
            "/stats?temper=true&cpu=true",
        });
      }
    },
    uptimeToStr(timeUp) {
      let str = "";

      let days = Math.floor(timeUp / 86400);

      if (days) {
        days = days < 10 ? ("0" + days).slice(-2) : days;
        str += days > 0 ? days + " " + this.translate("date.d") + " " : "";
      }

      let hours = Math.floor((timeUp % 86400) / 3600);
      if (hours) {
        str += ("0" + hours).slice(-2) + " " + this.translate("date.h") + " ";
      }

      if (!days || hours === 0) {
        let min = Math.floor((timeUp % 3600) / 60);
        str += ("0" + min).slice(-2) + " " + this.translate("date.min") + " ";
      }

      if (!days && !hours) {
        let sec = Math.floor(timeUp % 60);
        str += ("0" + sec).slice(-2) + " " + this.translate("date.sec") + " ";
      }

      return str;
    },
    get_minimap() {
      this.minimap.show = false
      this.minimap.city = null;
      this.minimap.country = null;
      this.minimap.source = ""
      this.$http.v1.getDeviceLocation(this.device.fid, (response) => {
        if (response.data.long === null) {
          this.minimap.show = false;
          this.minimap.city = null;
          this.minimap.country = null;
        } else {
          this.minimap.show = true;
          this.minimap.coords = {
            lat: +response.data.lat,
            lng: +response.data.long,
          };
          this.minimap.city = response.data.city;
          this.minimap.country = response.data.country;
          this.minimap.ts = response.data.ts;
          this.minimap.source = response.data.source;
          this.minimap.info =
            this.get_date(this.minimap.ts) +
            "<br/>" +
            this.minimap.source +
            "<br/>" +
            this.minimap.coords.lat +
            " " +
            this.minimap.coords.lng +
            "<br/>"
          ;
        }
      });
    },
    add_coordinates_manually(){
      this.$http.v1.getDeviceCoord(this.device.fid, (response) => {
        let t = this.$createElement(DeviceCoord, {
          props: {
            coords: { lat: response.data.lat, long: response.data.long, address: response.data.address},
            device_id: this.device.id,
            device_fid: this.device.fid
          },
          on: {
            close: () => {
              this.$modal.hide("version_history_modal");
            },
            update_coords: () => {
              this.get_minimap()
              this.$modal.hide("version_history_modal");
            },

          },
        });
        let temp = {
          render() {
            return t;
          },
        };
        this.$modal.show(
            temp,
            {},
            {
              height: "auto",
              adaptive: true,
              clickToClose: true,
              scrollable: true,
              name: "version_history_modal",
            }
        );
      })

    },
    show_version_history() {
      let t = this.$createElement(HistoryModal, {
        props: {
          device: this.device,
        },
        on: {
          close: () => {
            this.$modal.hide("version_history_modal");
          },
        },
      });
      let temp = {
        render() {
          return t;
        },
      };
      this.$modal.show(
        temp,
        {},
        {
          height: "auto",
          adaptive: true,
          clickToClose: true,
          scrollable: true,
          name: "version_history_modal",
        }
      );
    },

    show_rpc() {
      this.$http.v1.getRPCInfo(this.device.fid, (response) => {
        let t = this.$createElement(RPCModal, {
          props: {
            history: response.data.cmd_history,
            list: response.data.cmd_list,
            fid: this.fid,
          },
          on: {
            close: () => {
              this.$modal.hide("rpc_modal");
            },
          },
        });
        let temp = {
          render() {
            return t;
          },
        };
        this.$modal.show(
          temp,
          {},
          {
            width: 1100,
            height: "auto",
            adaptive: true,
            clickToClose: true,
            scrollable: true,
            name: "rpc_modal",
          }
        );
      });
    },
    new_ticket(val) {
      let t = this.$createElement(NewTicket, {
        props: { ticket: val, device_id: this.device.id },
        on: {
          close: () => {
            this.$modal.hide("new_ticket");
          },
          update: () => {
            this.$store.dispatch('updateDeviceProfile', this.device.fid)
            this.$modal.hide("new_ticket");
          }
        },
      });
      let temp = {
        render() {
          return t;
        },
      };
      this.$modal.show(
        temp,
        {},
        {
          height: "auto",
          adaptive: true,
          clickToClose: true,
          scrollable: true,
          name: "new_ticket",
        }
      );
    },
    copy(item) {
      this.copy_to_clipboard(item);
      this.global_notify("info", this.translate("clipboard.value_save_buffer"));
    },

    edit_device_profile() {
      let t = this.$createElement(EditDeviceProfile, {
        props: {
          device: this.device,
          client_info: this.client_info,
          vlan: this.network_settings.vlan
        },
        on: {
          close: () => {
            this.$modal.hide("edit_device_profile");
          },
          update: () => {
            this.get_network_settings()
            this.$modal.hide("edit_device_profile");
          },
        },
      });
      let temp = {
        render() {
          return t;
        },
      };
      this.$modal.show(
        temp,
        {},
        {
          height: "auto",
          width: 800,
          adaptive: true,
          clickToClose: true,
          scrollable: true,
          name: "edit_device_profile",
        }
      );
    },

    blockedDevice() {
      let t = this.$createElement(BlockDevice, {
        props: {
          fid: this.fid,
          question:
            this.device.status_info.block?.status === 0
              ? this.translate("device.ask_block")
              : this.translate("device.ask_unblock"),
          status: this.device.status_info.block?.status,
        },
        on: {
          close: () => {
            this.$modal.hide("dynamic-modal");
          },
        },
      });
      let temp = {
        render() {
          return t;
        },
      };
      this.$modal.show(
        temp,
        {},
        {
          height: "auto",
          adaptive: true,
          clickToClose: false,
          scrollable: true,
          name: "dynamic-modal",
        }
      );
    },

    check_group(
      parent,
      group,
      id,
      devs_ids,
      groups_for_dev_ids,
      groups_ids,
      groups_for_grp_ids
    ) {
      let new_group = {
        id: +group.id,
        expanded: false,
        hasExpanded: true,
        parent: group.parent,
        name: group.name,
        children: [],
        role: "group",
      };
      if (groups_ids.indexOf(+group.id) > -1) {
        new_group.checked = true;
        new_group.children.push({
          role: "empty",
          visible: false,
          checked: true,
        });
      } else {
        new_group.checked = false;
        new_group.children.push({ role: "empty", visible: false });
      }

      parent.children[id] = new_group;

      if (
        groups_for_dev_ids.indexOf(parent.children[id].id) > -1 ||
        groups_for_grp_ids.indexOf(parent.children[id].id) > -1
      ) {
        parent.children[id].expanded = true;
        this.$http.v1.getListDevicesByIdGroup(
          { group_id: group.id },
          (response) => {
            let devices = response.data;
            for (let j in devices) {
              let device = {
                name:
                  "(" +
                  devices[j].fid +
                  ") " +
                  "<b>" +
                  devices[j].name2 +
                  "</b>",
                id: devices[j].device_id,
                role: "children",
                group_id: +group.id,
                fid: devices[j].fid,
              };
              devs_ids.indexOf(+devices[j].device_id) > -1
                ? (device.checked = true)
                : (device.checked = false);

              parent.children[id].children.push(device);
            }
          }
        );
      }

      if (group.children) {
        for (let children_id in group.children) {
          this.check_group(
            parent.children[id],
            group.children[children_id],
            children_id,
            devs_ids,
            groups_for_dev_ids,
            groups_ids,
            groups_for_grp_ids
          );
        }
      }
    },

    notification(device) {
      let devs_ids = [device.id];
      let groups_for_dev_ids = [device.user_info.group_id];

      let notice_list = {};
      notice_list.devices = [];
      groups_for_dev_ids = groups_for_dev_ids.concat(
        ...this.$store.getters.getPathGroupById(device.user_info.group_id)
      );

      notice_list.devices.push({
        name: "(" + device.fid + ") " + "<b>" + device.user_info.name2 + "</b>",
        name2: device.user_info.name2,
        id: device.id,
        role: "children",
        group_id: +device.user_info.group_id,
        checked: true,
        fid: device.fid,
      });

      notice_list.status = 1

      let groups = this.$store.getters.getDeviceGroupsTree;

      let group_list = { children: [] };
      for (let group_id in groups) {
        this.check_group(
          group_list,
          groups[group_id],
          group_id,
          devs_ids,
          groups_for_dev_ids,
          [],
          []
        );
      }

      let t = this.$createElement(NotificationEdit, {
        props: {
          notice: notice_list,
          list_devices: group_list.children,
          source: "device",
        },
        on: {
          close: () => {
            this.$modal.hide("dynamic-modal");
          },
        },
      });
      let temp = {
        render() {
          return t;
        },
      };
      this.$modal.show(
        temp,
        {},
        {
          height: "auto",
          width: 800,
          adaptive: true,
          clickToClose: false,
          scrollable: true,
          name: "dynamic-modal",
        }
      );
    },

    open_speedometer() {
      this.$http.v1.getDeviceTestDeviceStatus(this.device.fid, (response) => {
        if (response.data.uptime.status === 1) {
          let ifaces = Object.keys(response.data.ifaces).map((key) => {
            return { id: key, label: response.data.ifaces[key] }
          });

          let iperf_servers = response.data.iperf_servers.map((elem) => {
            return { id: elem.ip, label: elem.name }
          });

          let t = this.$createElement(SpeedTest, {
            props: {
              fid: this.device.fid,
              sim_list: ifaces,
              version: this.device.device_info.version?.fw,
              iperf_servers: iperf_servers,
              duration: response.data.duration,
              threads: response.data.threads,
              enable_test: response.data.enable_test || false,
            },
            on: {
              close: () => {
                this.$modal.hide("dynamic-modal");
              },
            },
          });
          let temp = {
            render() {
              return t;
            },
          };
          this.$modal.show(
            temp,
            {},
            {
              height: "auto",
              adaptive: true,
              clickToClose: true,
              scrollable: true,
              name: "dynamic-modal",
            }
          );
        } else {
          this.global_notify("info", this.translate("http.device_offline"));
        }
      });
    },
  },
};
</script>

<style lang='scss' scoped>
.device_profile {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  overflow: auto;

  &__left {
    width: 70%;
    height: 100%;
    padding-right: 10px;

    .line {
      margin-bottom: 10px;
    }

    .refer_info {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 5px;

      .version {
        flex: 1;
        min-width: 0;
        font-size: 12px;
        color: #9a9a9a;
        display: block;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .overlays {
        margin: 0 10px;
      }

      .icons {
        flex-shrink: 0;
        display: flex;
        .icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          font-size: 24px;
          border: 2px solid #66615b;
          border-radius: 5px;
          margin: 0 2px;
          cursor: pointer;
          transition: 0.15s;
          &:hover {
            border-color: #41b883;
            color: #41b883;
          }
          &.manual{
            background-color: #41b88330;
          }
          &.new_tickets{
            background-color: #ffb05850;
          }
        }
      }
    }

    .problems {
      .blocked {
        border-color: #b84141;
        background-color: #b8414130;
        color: #b84141;
        padding: 10px;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        p {
          font-size: 14px;
          line-height: 120%;
          margin: 0;
        }
        .block_left{
          width: 75%;
        }
        .block_right{
          a{
            color: #b84141;
            font-size: 14px;
          }
        }
      }
    }

    .billing {
      display: flex;
      align-items: stretch;
      justify-content: space-between;

      @media (max-width: 768px) {
        flex-direction: column;
      }

      .card {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: 0 5px;
        padding: 10px;
        &:last-child{
          margin-right: 0;
        }
        &:first-child{
          margin-left: 0;
        }
      }

      .tariff{
        flex: 2;
        display: flex;
        flex-direction: column;
        gap: 5px;
        &__line{
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;

          .left{
            text-align: left;
            width: 75%;
          }

          .right{
            text-align: right;
          }

          .tariffication{
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 5px;
            .mdi{
              font-size: 22px;
              color: #3f93d3;
            }
          }

          .mdi{
            font-size: 22px;
          }

          .diff{
            color: #ffb058;
          }
        }

        a,p {
          font-size: 12px;
          line-height: 120%;
          margin: 0;
          @media (max-width: 1500px) {
            font-size: 10px;
          }
        }
        @media (max-width: 768px) {
          flex: auto;
          width: 100%;
          margin: 0 0 10px;
        }
      }

      .traffic__block{
        display: flex;
        align-items: stretch;
        justify-content: center;
        padding: 5px;
        flex: 1;
        @media (max-width: 768px) {
          flex: auto;
          width: 100%;
          margin: 0 0 5px;
        }
        .traffic__wrap{
          width: 100%;
          .traffic_line{
            border-bottom: 1px solid #dfdfdf;
            padding: 5px;
            &:last-child{
              border-bottom: none;
            }
          }
        }

        .tariff_name, .rest{
          font-size: 12px;
          text-align: center;
          margin-bottom: 0;
        }

        .graph{
          &_wrap{
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            .percent{
              width: 60px;
              margin-right: 10px;
            }
            .mdi{
              font-size: 20px;
            }
          }
          display: block;
          position: relative;
          width: 100%;
          background: #D5E7FF;
          height: 10px;
          border-radius: 2px;
          overflow-x: hidden;
          margin: 5px 0;
          &::after{
            content: '';
            display: block;
            position: absolute;
            top: 0;
            left: 0;
            width: var(--lw);
            height: 10px;
            background: var(--c);
            border-radius: 2px 0 0 2px;
          }
        }
      }

      .client_order{
        flex: 2;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: space-between;

        @media (max-width: 768px) {
          flex: auto;
          width: 100%;
          margin: 0 0 5px;
        }

        &__line{
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .order{
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: space-between;
        }
        a, p{
          max-width: 250px;
          font-size: 12px;
          margin-bottom: 0;
          @media (max-width: 1500px) {
            font-size: 10px;
          }
        }
        .mdi{
          font-size: 22px;
        }
      }

      .traffic{
        width: 100%;
      }

      .roaming{
        width: 20%;
        .mdi{
          border-color: inherit;
          background-color: inherit;
          font-size: 22px;
          padding: 0;
        }
      }

      .off {
        border-color: #b84141;
        background-color: #b8414130;
      }
      .on {
        border-color: #41b883;
        background-color: #41b88330;
      }
      .ended{
        border-color: #ffb058;
        background-color: #ffb05830;
      }

      .mdi-currency-rub {
        display: inline-flex;
        justify-content: center;
        align-items: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
      }

      .add_packet{
        margin: 5px;
        padding: 7px 10px;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    .ifaces{
      display: flex;
      align-items: center;
      justify-content: flex-start;
      width: 100%;
      overflow-x: auto;
      padding-bottom: 3px;
      .card {
        width: 100%;
        height: 100%;
        margin: 0 5px;
        padding: 7.5px;
        max-width: 150px;
        min-width: 150px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        overflow: hidden;
        &:last-child{
          margin-right: 0;
        }
        &:first-child{
          margin-left: 0;
        }
        .top{
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        p{
          font-size: 12px;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0;
        }
      }

      .inactive_color {
        border-color: #919191;
        background-color: #91919130;
        color: #919191;
      }
      .active_color {
        border-color: #41b883;
        background-color: #41b88330;
        color: #41b883;
      }
      .inactive_back {
        background-color: #91919130;
      }
      .active_back {
        background-color: #41b88330;
      }
    }

    .simcard_list {
      .response_table {
        margin: 0;
      }
    }

    .main_info {
      display: flex;
      justify-content: flex-end;
      align-items: stretch;
      flex-wrap: wrap;
      gap: 10px;
      overflow-wrap: anywhere;

      &__wrap{
        display: flex;
        flex-direction: column;
        flex: auto;
        &:nth-child(3){
          width: 100% !important;
        }
      }
      &--two-columns{
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        .main_info__wrap{
          min-width: 0;
        }
      }
      .header_main_info {
        margin-bottom: 5px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        min-height: 25px;
        gap: 10px;
        .title {
          font-size: 12px;
          line-height: 120%;
          font-weight: bold;
          margin-bottom: 0;
        }
      }
      .card {
        padding: 10px 30px 10px 10px;
        background-color: #fff;
        position: relative;
        flex: 1;

        &.contacts{
          overflow: hidden;
          .contact{
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            flex-wrap: wrap;

            span{
              margin-right: 5px;
            }

            a{
              margin-bottom: 0;
            }
          }
        }
        .edit {
          position: absolute;
          top: 5px;
          right: 5px;
          width: 20px;
          height: 20px;
          text-align: center;
          vertical-align: middle;
          font-size: 20px;
          color: #66615b;
          transition: 0.2s;
          cursor: pointer;
          &:hover {
            color: #41b883;
          }
        }

        .groups,
        .client {
          display: flex;
          .links {
            min-width: 0;
            display: flex;
            flex-wrap: wrap;
            margin-left: 5px;
            .link {
              min-width: 0;
              display: flex;
              align-items: baseline;
              gap: 5px;
              margin: 0;
            }
            .delim{
              margin: 0 5px;
              font-size: 16px;
            }
          }
        }
        a {
          font-size: 14px;
          color: #41b883;
          display: block;
          margin-bottom: 5px;
          line-height: 1.4em;
          position: relative;
          &:hover {
            color: #41b883cc;
          }
          &:last-child {
            margin-right: 0;
          }
        }
        p {
          font-size: 14px;
          margin-bottom: 5px;
          white-space: pre-line;
        }
      }
    }

    .place {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      .w40 {
        width: 40%;
      }
      .w20 {
        width: 20%;
        .btn-100 {
          width: 100%;
        }
      }

      .form-group--wrap {
        margin: 0 5px;
        &:first-child {
          margin-left: 0;
        }
        &:last-child {
          margin-right: 0;
        }
        .form-group {
          margin: 0;
        }
      }
    }
  }

  &__right {
    width: 30%;
    height: 100%;
  }
}

@media (max-width: 1200px) {
  .device_profile {
    flex-direction: column;

    &__left {
      width: 100%;
      height: auto;
    }

    &__right {
      width: 100%;
      height: auto;
    }
  }
}

@media (max-width: 500px) {
  .device_profile {
    &__left {
      .refer_info {
        .version {
          width: 60%;
        }
        .icons {
          width: 40%;
          flex-wrap: wrap;
          justify-content: flex-end;
          .icon {
            margin: 2px;
          }
        }
      }

      .main_info {
        .card {
          &__left {
            width: 100%;
          }
          &__right {
            width: 100%;
          }
        }
      }
    }
  }
}
</style>
